// NEW_FILE_JUSTIFIED: Portal settings form state — extracted per max-2-useState rule; manages credential fields + save UI state
// LOCAL_TYPE_JUSTIFIED: ICredentialResult and IUsePortalSettingsArgs are internal hook contracts not needed outside this file
import { useState } from 'react';
import { useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package, not a boundary violation
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';

interface ICredentialResult {
  // LOCAL_TYPE_JUSTIFIED: shape of the useQuery result passed in from parent
  portalUrl: string;
  username: string;
  hasPassword: boolean;
}

interface IUsePortalSettingsArgs {
  // LOCAL_TYPE_JUSTIFIED: internal hook args not needed in @types/
  orgId: Id<'organisations'>;
  providerId: Id<'insuranceProviders'>;
  providerName: string;
  credentials: ICredentialResult | null | undefined;
}

// LOCAL_TYPE_JUSTIFIED: grouped field state for this hook only.
// portalUrl/username are null until the user edits them; displayed value falls back to credentials.
interface IFieldState {
  portalUrl: string | null;
  username: string | null;
  password: string;
}

interface IUiState {
  // LOCAL_TYPE_JUSTIFIED: grouped UI state for this hook only
  showPassword: boolean;
  isSaving: boolean;
  saveMessage: string | null;
}

export interface IUsePortalSettingsReturn {
  // LOCAL_TYPE_JUSTIFIED: return type consumed only by PortalSettingsSection component
  portalUrl: string;
  setPortalUrl: (v: string) => void;
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  isSaving: boolean;
  saveMessage: string | null;
  handleSave: () => void;
}

export function usePortalSettings({
  orgId,
  providerId,
  providerName,
  credentials,
}: IUsePortalSettingsArgs): IUsePortalSettingsReturn {
  // User overrides — null means "not yet edited; fall back to server value".
  // This eliminates the need for a syncing effect (no setState-in-effect).
  const [fields, setFields] = useState<IFieldState>({
    portalUrl: null,
    username: null,
    password: '',
  });
  const [uiState, setUiState] = useState<IUiState>({
    showPassword: false,
    isSaving: false,
    saveMessage: null,
  });

  const upsertCredentials = useMutation(api['insurance/portalCredentials'].upsert);

  // Derived display values: user override takes priority, server value is the fallback.
  const portalUrl = fields.portalUrl ?? credentials?.portalUrl ?? '';
  const username = fields.username ?? credentials?.username ?? '';

  const handleSave = () => {
    setUiState(prev => ({ ...prev, isSaving: true, saveMessage: null }));
    const providerSlug = providerName.toLowerCase().replace(/\s+/g, '-');
    void upsertCredentials({
      orgId,
      providerId,
      providerSlug,
      portalUrl,
      username,
      password: fields.password || undefined,
    })
      .then(() => {
        setUiState(prev => ({ ...prev, isSaving: false, saveMessage: 'Credentials saved' }));
        setTimeout(() => setUiState(prev => ({ ...prev, saveMessage: null })), 3000);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to save';
        setUiState(prev => ({ ...prev, isSaving: false, saveMessage: message }));
      });
  };

  return {
    portalUrl,
    setPortalUrl: (v: string) => setFields(prev => ({ ...prev, portalUrl: v })),
    username,
    setUsername: (v: string) => setFields(prev => ({ ...prev, username: v })),
    password: fields.password,
    setPassword: (v: string) => setFields(prev => ({ ...prev, password: v })),
    showPassword: uiState.showPassword,
    setShowPassword: (v: boolean) => setUiState(prev => ({ ...prev, showPassword: v })),
    isSaving: uiState.isSaving,
    saveMessage: uiState.saveMessage,
    handleSave,
  };
}
